'use client'

import { ArrowBtn } from '@/app/components/atoms/ArrowBtn'
import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA, NOT_SET } from '@/app/lib/analytics/constants'
import { withIndex } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import type { ContextualProductListAnalytics } from '@/app/lib/analytics/types'
import { useProductListTracking } from '@/app/lib/analytics/useProductListTracking'
import { useDragScroll } from '@/app/lib/hooks/useDragScroll'
import { useRailScroll } from '@/app/lib/hooks/useRailScroll'
import { cx } from '@/app/lib/utils'

import { OtherLeftProductCard } from './OtherLeftProductCard'
import type { OtherLeftProductCardData } from './OtherLeftProductCard/types'
import styles from './OtherLeftProductRail.module.scss'

interface OtherLeftProductRailProps {
  readonly items: readonly OtherLeftProductCardData[]
  /**
   * Analítica del carril (`view_item_list`/`select_item`), con `analytics.items`
   * alineado con `items`. Opcional: sin ella (módulo CMS general, tests) el
   * carril no emite nada, como hasta ahora.
   */
  readonly analytics?: ContextualProductListAnalytics
}

/**
 * Carril de tarjetas del módulo "otros productos izquierda": en móvil/tablet
 * es una rejilla estática de 2 columnas (gobernada por CSS, sin JS); desde
 * escritorio (`@include breakpoint(desktop)`, ver
 * `OtherLeftProductRail.module.scss`) pasa a un carril horizontal con
 * `scroll-snap` nativo y dos `ArrowBtn` superpuestas que avanzan/retroceden
 * una tarjeta por click.
 *
 * Promovido desde `modules/OtrosProductosIzq/ProductRail.tsx` (renombrado,
 * sin cambios de lógica — specs/051-canales-modulo-otros-productos-izq/
 * research.md R-01): lo consumen tanto el módulo CMS general
 * (`modules/OtrosProductosIzq/`) como el módulo de canal
 * (`channelModules/OtherLeftProduct/`).
 *
 * `'use client'` únicamente por esto: los módulos que lo consumen
 * (`OtrosProductosIzq.tsx`/`OtherLeftProduct.tsx` de canal) son Server
 * Components — la interactividad queda empujada a esta hoja (Constitución,
 * Principio I).
 *
 * La mecánica de scroll (medir el paso, saber si estamos en cada extremo,
 * reaccionar a resize) vive en `useRailScroll`, compartida con el carril de
 * `ProductosBotonCompra` — aquí solo queda qué tarjeta se pinta y dónde van las
 * flechas (specs/031-productos-boton-compra-module/research.md §4). Las flechas
 * se ocultan en cada extremo del recorrido, sin nada más que descubrir en ese
 * sentido (FR-010/FR-011 de cada spec).
 */
export function OtherLeftProductRail({ items, analytics }: OtherLeftProductRailProps) {
  const { railRef, atStart, atEnd, recalc, scrollByStep } = useRailScroll(items.length)
  // Con ratón el carril no se arrastra de forma nativa (solo el gesto táctil).
  useDragScroll(railRef, items.length)

  // Los hooks no pueden ser condicionales: sin `analytics` se instancia con
  // valores neutros y sin trackers (no-op), y las cards no se envuelven.
  const pageAnalytics = useOptionalPageAnalytics()
  const { trackImpression, trackSelect } = useProductListTracking(
    {
      // `<page_type>_<module_type>` (`Listado filtrado.csv`), igual que `RecentlyViewed`.
      itemListId: `${pageAnalytics?.context.page_type ?? NOT_SET}_${analytics?.moduleType ?? NO_APLICA}`,
      itemListName: analytics?.itemListName ?? NO_APLICA,
      moduleName: analytics?.moduleName ?? NO_APLICA,
      moduleType: analytics?.moduleType ?? NO_APLICA,
      modulePosition: analytics?.modulePosition ?? NO_APLICA,
    },
    analytics
      ? { trackViewItemList: pageAnalytics?.trackViewItemList, trackSelectItem: pageAnalytics?.trackSelectItem }
      : {},
  )

  return (
    <div className={styles.viewport}>
      {/* `data-testid`: única forma práctica de localizar el contenedor
          desplazable desde el test — no expone rol/nombre accesible propio
          (el carril no es una landmark, sus tarjetas ya son enlaces
          alcanzables individualmente por rol). */}
      <div
        ref={railRef}
        className={styles.rail}
        onScroll={recalc}
        data-testid="product-rail"
      >
        {items.map(({ id, ...card }, index) => {
          const item = analytics?.items[index]
          const cardElement = <OtherLeftProductCard {...card} />

          return (
            <div key={id} className={styles.item}>
              {item ? (
                <TrackedProductCard
                  index={index}
                  onImpression={() => trackImpression(item.item_id, withIndex(item, index))}
                  onSelect={() => trackSelect(withIndex(item, index))}
                >
                  {cardElement}
                </TrackedProductCard>
              ) : (
                cardElement
              )}
            </div>
          )
        })}
      </div>

      <div className={styles.nav}>
        {!atStart && (
          <ArrowBtn
            dir="left"
            overlay
            className={cx(styles.navBtn, styles.navBtnLeft)}
            onClick={() => scrollByStep(-1)}
          />
        )}
        {!atEnd && (
          <ArrowBtn
            dir="right"
            overlay
            className={cx(styles.navBtn, styles.navBtnRight)}
            onClick={() => scrollByStep(1)}
          />
        )}
      </div>
    </div>
  )
}
